import type { IconType } from 'react-icons';
import {
  FiActivity,
  FiAlertTriangle,
  FiBarChart2,
  FiCalendar,
  FiDollarSign,
  FiFileText,
  FiMapPin,
  FiShield,
  FiUsers,
} from 'react-icons/fi';
import type { EntitlementKey } from '../services/billingService';
import {
  hasPermission,
  isSystemAdministrator,
  type StaffAccess,
  type StaffPermission,
  type WorkspaceTier,
} from '../services/roleService';

/**
 * The workspace sidebar: what is in it, and who it is shown to.
 *
 * `navItems` is the sidebar's source of truth. It lives here rather than in
 * DashboardShell so that who-sees-what can be tested without rendering every
 * page of the workspace.
 */

/**
 * Who an item is for. Leave both out and everybody in the workspace sees it.
 *
 * `permission` is something a company can give to a management role, and
 * `adminOnly` is what stays with System Administrators whatever a role says:
 * pay rates, billing, privacy reviews and the roles themselves. For payroll,
 * clients and staff the database refuses a role without the permission, so
 * hiding the item is signposting. For the other areas this is the gate.
 */
export interface StaffGate {
  permission?: StaffPermission;
  adminOnly?: boolean;
}

export interface SubItem extends StaffGate {
  label: string;
  subIndex: number;
  summary?: string;
  /**
   * Omit for anything Core includes. Named entitlements are hidden on a plan
   * that does not hold them; the real block is the RLS policy on the tables
   * behind the page, so this is signposting rather than security.
   */
  entitlement?: EntitlementKey;
}

export type WorkspaceRole = WorkspaceTier;

export interface NavItem extends StaffGate {
  label: string;
  index: number;
  icon: IconType;
  summary: string;
  subItems?: SubItem[];
  /** See SubItem.entitlement. */
  entitlement?: EntitlementKey;
}

export const navItems: NavItem[] = [
  {
    label: 'Ops Centre',
    icon: FiActivity,
    summary: 'Immediate priorities and live field visibility',
    index: 0,
    permission: 'ops_centre',
    subItems: [
      { label: 'Home', subIndex: 2, summary: 'Immediate operational priorities' },
      { label: 'Live View', subIndex: 0, summary: 'Live guard positions' },
      { label: 'Shift History', subIndex: 1, summary: 'Past shift records' },
    ],
  },
  {
    label: 'Schedule',
    icon: FiCalendar,
    summary: 'Duty coverage, roster design and leave planning',
    index: 5,
    permission: 'schedule',
    subItems: [
      { label: 'Duty Board', subIndex: 7, summary: 'Quick duty + guard assignment' },
      { label: 'Shifts', subIndex: 8, summary: 'Merged guard-facing roster view' },
      { label: 'Leave', subIndex: 9, summary: 'Approve and manage guard leave' },
    ],
  },
  {
    label: 'Alarms',
    icon: FiAlertTriangle,
    summary: 'Panic alerts and response queue',
    index: 9,
    permission: 'alarms',
    subItems: [
      { label: 'Active', subIndex: 0, summary: 'Open and acknowledged alarms' },
      { label: 'History', subIndex: 1, summary: 'Past alarm records' },
    ],
  },
  {
    label: 'Staff',
    icon: FiShield,
    summary: 'Team directory and licence checks',
    index: 1,
    permission: 'staff',
    subItems: [
      { label: 'All Staff', subIndex: 0, summary: 'People and contact details' },
      { label: 'Licence Verification', subIndex: 1, summary: 'Compliance monitoring' },
    ],
  },
  {
    label: 'Reports',
    icon: FiFileText,
    summary: 'Incident reviews and shift summaries',
    index: 2,
    permission: 'reports',
    subItems: [
      { label: 'Incident Reports', subIndex: 0, summary: 'Incident triage and reviews' },
      { label: 'Summary Reports', subIndex: 1, summary: 'Daily reports, patrol reports and visitor logs' },
    ],
  },
  {
    label: 'Analytics',
    icon: FiBarChart2,
    summary: 'Performance, workload and contract margins',
    index: 4,
    permission: 'analytics',
    entitlement: 'operational_analytics',
    subItems: [
      { label: 'Operations', subIndex: 0, summary: 'Performance and workload trends' },
      // What clients are charged against what staff are paid: commercial, like pay bands.
      { label: 'Profitability', subIndex: 1, summary: 'Cost and margin per client contract', adminOnly: true },
    ],
  },
  { label: 'Sites', icon: FiMapPin, summary: 'Sites, patrols and posts', index: 6, permission: 'sites' },
  {
    label: 'Payroll',
    icon: FiDollarSign,
    summary: 'Pay runs, timesheets and pay bands',
    index: 11,
    permission: 'payroll',
    entitlement: 'payroll_export',
    subItems: [
      { label: 'Timesheets', subIndex: 1, summary: 'Prepare, review and lock a pay period' },
      { label: 'Gross pay preview', subIndex: 0, summary: 'Preview live calculations and CSV layouts' },
      // Rates are commercial control, so bands stay with System Administrators.
      { label: 'Pay bands', subIndex: 2, summary: 'Pay grades, rates and assignments', adminOnly: true },
    ],
  },
  {
    label: 'Admin',
    icon: FiUsers,
    summary: 'Clients and admin access',
    index: 10,
    // No gate of its own: it is shown to whoever can open at least one tab.
    subItems: [
      { label: 'Clients', subIndex: 0, summary: 'Client onboarding and portal access', permission: 'clients', entitlement: 'client_portals' },
      { label: 'Cover Requests', subIndex: 3, summary: 'Extra cover raised from client portals', permission: 'clients', entitlement: 'client_portals' },
      { label: 'Roles & Permissions', subIndex: 5, summary: 'What each role can open, and who owns the workspace', adminOnly: true },
      { label: 'Account Management', subIndex: 1, summary: 'Company administrator accounts', adminOnly: true },
      { label: 'Billing', subIndex: 4, summary: 'Active users this period and what they cost', adminOnly: true },
      { label: 'Privacy Reviews', subIndex: 2, summary: 'Account deletion and retention decisions', adminOnly: true },
    ],
  },
];

export const isVisibleToStaff = (item: StaffGate, access: StaffAccess): boolean => (
  (!item.adminOnly || isSystemAdministrator(access))
  && (!item.permission || hasPermission(access, item.permission))
);

const isVisibleOnPlan = <T extends { entitlement?: EntitlementKey }>(
  item: T,
  has: (key: EntitlementKey) => boolean,
): boolean => !item.entitlement || has(item.entitlement);

export const resolveRootIndex = (selectedIndex: number): number => {
  if (selectedIndex === 1.5 || selectedIndex === 1.7) return 1;
  if (selectedIndex === 7 || selectedIndex === 7.5) return 6;
  return selectedIndex;
};

/** Settings is everybody's, and is where somebody with nothing else lands. */
export const SETTINGS_INDEX = 8;

/** The first area these roles open: where the workspace starts. */
export const landingFor = (access: StaffAccess): { index: number; subIndex: number } => {
  for (const item of navItems) {
    if (!isVisibleToStaff(item, access)) continue;
    if (!item.subItems) return { index: item.index, subIndex: 0 };

    const tabs = item.subItems.filter((subItem) => isVisibleToStaff(subItem, access));
    // Ops Centre opens on Home, which is not its first tab.
    if (item.index === 0) return { index: 0, subIndex: 2 };
    if (tabs.length > 0) return { index: item.index, subIndex: tabs[0].subIndex };
  }
  return { index: SETTINGS_INDEX, subIndex: 0 };
};

export interface WorkspaceNav {
  /** Sidebar items, in display order, that these roles and this plan open. */
  visibleNavItems: NavItem[];
  /** The tabs of one section that these roles and this plan open. */
  tabsFor: (index: number) => SubItem[];
  /**
   * Whether these roles may see an area. The sidebar only offers what they
   * may, but a notification, an alarm banner or a tour can still point
   * somewhere else, and this is what stops the page being drawn.
   */
  canOpen: (rootIndex: number, subIndex?: number) => boolean;
}

export function resolveWorkspaceNav(
  access: StaffAccess,
  hasEntitlement: (key: EntitlementKey) => boolean,
): WorkspaceNav {
  const tabsFor = (index: number): SubItem[] => (
    navItems.find((item) => item.index === index)?.subItems
      ?.filter((subItem) => isVisibleToStaff(subItem, access) && isVisibleOnPlan(subItem, hasEntitlement)) ?? []
  );

  const visibleNavItems = navItems.filter((item) => (
    isVisibleToStaff(item, access)
    && isVisibleOnPlan(item, hasEntitlement)
    // A section whose every tab is closed to these roles is closed too.
    && (!item.subItems || tabsFor(item.index).length > 0)
  ));

  const canOpen = (rootIndex: number, subIndex?: number): boolean => {
    if (rootIndex === SETTINGS_INDEX) return true;
    const item = visibleNavItems.find((navItem) => navItem.index === rootIndex);
    if (!item) return false;
    if (!item.subItems || subIndex === undefined) return true;
    return tabsFor(rootIndex).some((tab) => tab.subIndex === subIndex);
  };

  return { visibleNavItems, tabsFor, canOpen };
}
